import React from "react";

import { Button } from "~/components/button";
import { IconLockOutlined } from "~/components/icon";
import { Truncated } from "~/components/truncated";
import type { LockedDataPopoverEvent } from "~/tools/analytics/locked-data-popover-event";
import { useCapture } from "~/tools/analytics/use-capture";
import { cx } from "~/tools/class-variance";
import { secondsToMilliseconds } from "~/tools/date-time";
import { isDeepEqual } from "~/tools/is-deep-equal";
import { onTimeout } from "~/tools/on-timeout";
import { useEffect } from "~/tools/use-effect";

import { useUserProfile } from "../../common/use-user-profile";
import { DocLink } from "../react/docLink/DocLink";
import { InlineLink } from "../react/styled/StyledComponents";

const SHOW_CAPTURE_DELAY_MS = secondsToMilliseconds(1);

interface LockedItemPopoverContentProps {
	title: string;
	content: string;
	capture: LockedDataPopoverEvent;
	actionType?: "monitors" | "metrics" | "connections";
	className?: string;
}

export const LockedItemPopoverContent: React.FC<LockedItemPopoverContentProps> = ({
	title,
	content,
	actionType,
	capture: captureEvent,
	className,
}) => {
	const capture = useCapture();
	const [, updateUserProfile] = useUserProfile();

	const onHide = () => {
		updateUserProfile((userProfile) => {
			if (actionType === "monitors") {
				return { ...userProfile, hideUnavailableMonitors: true };
			}
			if (actionType === "metrics") {
				return { ...userProfile, hideUnavailableMetrics: true };
			}
			if (actionType === "connections") {
				return { ...userProfile, hideUnavailableConnections: true };
			}

			return userProfile;
		});

		capture.clickOnHideLockedData({ location: captureEvent.location });
	};

	useEffect(
		() => {
			// make sure that the popover is visible before tracking the event
			return onTimeout(() => {
				capture.showLockedDataPopover(captureEvent);
			}, SHOW_CAPTURE_DELAY_MS);
		},
		[capture, captureEvent],
		isDeepEqual,
	);

	return (
		<div className={cx("flex flex-col gap-3", className)}>
			<div className="flex items-center gap-3 text-gray-800">
				<IconLockOutlined className="shrink-0 stroke-[1.5px] text-lg" />
				<Truncated className="font-semibold text-sm">{title}</Truncated>
			</div>

			<p className="mb-3 text-13 text-gray-600">
				{content}
				<DocLink
					capture={[capture.clickOnUpgradeToPrime, { location: captureEvent.location }]}
					slug="rancher-prime-upgrade"
					theme="primary"
				>
					Upgrade to Rancher Prime
				</DocLink>{" "}
				and enjoy faster troubleshooting.
			</p>

			<DocLink
				capture={[capture.clickOnLearnMore, { location: captureEvent.location }]}
				slug="rancher-prime-learn-more"
			>
				<Button className="uppercase" layout="block" variant="tertiary">
					Learn more
				</Button>
			</DocLink>

			{actionType && (
				<InlineLink className="hocus:text-gray-600 text-gray-600 text-xs" onClick={onHide}>
					Hide unavailable {actionType}
				</InlineLink>
			)}
		</div>
	);
};
